if (!customElements.get("image-with-scrolling-text")) {
  customElements.define(
    "image-with-scrolling-text",
    class Storytelling extends HTMLElement {
      constructor() {
        super();
        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
      }

      connectedCallback() {
        // Defer init to ensure children are parsed (Safari fix)
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
          // Use rAF to ensure layout is ready
          requestAnimationFrame(() => this.init());
        }
      }

     makeOneVisible() {
        // Safari-safe rootMargin: use pixel values instead of percentages
        const bottomMargin = -(Math.round(window.innerHeight * 0.8)) + 'px';
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
            if (entry.isIntersecting) {
                const index = Array.from(this.items).indexOf(entry.target); 

                this.images.forEach(img => {
                img.style.opacity = '0';
                });

                if (this.images[index]) {
                this.images[index].style.opacity = '1';
                }
            }
            });
        }, {
            root: null,
            rootMargin: '-70px 0px ' + bottomMargin + ' 0px',
            threshold: 0
        });

        this.items.forEach(block => observer.observe(block));
     }

      onScroll() {
        if (!this.items || this.items.length === 0) return;

        const container = this.querySelector('.wt-image-with-scrolling-text__image'); 
        if (!container) return;

        const bgTriggerY = window.innerHeight * 0.5;
        const navBottom = 70;

        // Find the tile closest to (or just past) the bg trigger point
        let activeBgTile = this.items[0];
        for (let i = this.items.length - 1; i >= 0; i--) {
          const rect = this.items[i].getBoundingClientRect();
          if (rect.top <= bgTriggerY) {
            activeBgTile = this.items[i];
            break;
          }
        }

        // Update text column background
        if (this.textColumn) {
          const bgColor = activeBgTile.dataset.bgColor || '';
          this.textColumn.style.backgroundColor = bgColor || 'transparent';
        }

        // Update sticky time indicator (ticker + circle)
        if (this.tickerReels && this.tickerReels.length && this.stickyTimeCircle) {
          const time = activeBgTile.dataset.time || '';
          const circleColor = activeBgTile.dataset.timeCircle || '';
          const textColor = activeBgTile.dataset.timeTextColor || '';

          // Animate ticker digits
          const digits = time.replace(':', '');
          this.tickerReels.forEach((reel, i) => {
            const digit = parseInt(digits[i], 10);
            if (!isNaN(digit)) {
              const val = 'translateY(-' + (digit * 1.2) + 'em)';
              reel.style.webkitTransform = val;
              reel.style.transform = val;
            }
          });

          this.stickyTimeCircle.style.backgroundColor = circleColor;
          if (this.tickerContainer) {
            this.tickerContainer.style.color = textColor;
          }
        }

        // Smoothly fade out & release sticky ticker once the last tile's text
        // content scrolls above the ticker position. Uses a 120px fade zone so
        // the disappearance feels gradual rather than abrupt.
        if (this.stickyTimerEl && this.items.length) {
          const lastTile = this.items[this.items.length - 1];
          const lastTileText = lastTile.querySelector('.image-text');
          if (lastTileText) {
            const textBottom = lastTileText.getBoundingClientRect().bottom;
            const stickyTop = 130; // matches CSS top value
            const fadeZone = 120; // px over which opacity fades from 1→0

            if (textBottom < stickyTop) {
              // Fully past text — hide and unstick
              this.stickyTimerEl.style.opacity = '0';
              this.stickyTimerEl.style.position = 'relative';
            } else if (textBottom < stickyTop + fadeZone) {
              // In fade zone — interpolate opacity smoothly
              const progress = (textBottom - stickyTop) / fadeZone;
              this.stickyTimerEl.style.opacity = Math.max(0, Math.min(1, progress));
              this.stickyTimerEl.style.position = '';
            } else {
              // Above fade zone — fully visible & sticky
              this.stickyTimerEl.style.opacity = '1';
              this.stickyTimerEl.style.position = '';
            }
          }
        }

        this.items.forEach((item, i) => {
          const rect = item.getBoundingClientRect();
          const elementTop = rect.top;
          const distanceFromTarget = Math.abs(elementTop - navBottom);

          const maxDistance = window.innerHeight * 0.6;
          const opacity = 1 - Math.min(distanceFromTarget / maxDistance, 1);

          item.style.opacity = opacity;
        });
      }

      onResize(){
        if(!this.outer || !this.inners || this.inners.length === 0) return;

        this.outer.style.marginTop = '0px';
        this.outer.style.marginBottom = '0px';
      }

      init() {
        // Query all DOM elements here (not in constructor) for Safari compatibility
        this.images = this.querySelectorAll(".image-wrapper > *");
        this.items = this.querySelectorAll(".wt-image-with-scrolling-text__text > .custom-wt-image-with-scrolling-text-wrapper");
        this.textColumn = this.querySelector('.wt-image-with-scrolling-text__text');
        this.stickyTimeCircle = this.querySelector('.scroll-time-sticky__circle');
        this.tickerContainer = this.querySelector('.scroll-time-ticker');
        this.tickerReels = this.querySelectorAll('.scroll-time-ticker__reel');
        this.stickyTimerEl = this.querySelector('.scroll-time-sticky');

        // Set initial digit positions
        if (this.tickerReels.length) {
          this.tickerReels.forEach(reel => {
            const digit = parseInt(reel.dataset.digit, 10);
            if (!isNaN(digit)) {
              const val = 'translateY(-' + (digit * 1.2) + 'em)';
              reel.style.webkitTransform = val;
              reel.style.transform = val;
            }
          });
        }

        this.makeOneVisible();
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll(); 
        window.addEventListener('resize', this.onResize);
        this.outer = this.textColumn;
        this.inners = this.querySelectorAll('.wt-image-with-scrolling-text__text > .custom-wt-image-with-scrolling-text-wrapper');
        this.onResize();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
      }
    },
  );
}
